import type { ProductTypesModel } from 'pages/dashboard-farm/widget-product-types/widget-product-types.models';
import { combineEpics, ofType, type ActionsObservable, type Epic, type StateObservable } from 'redux-observable';
import type { AppState } from '@protector/entities/__legacy__/redux.types';
import { of } from 'rxjs';
import { catchError, concatMap, exhaustMap, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import type { AbandonedAreasModel } from '../../../../pages/dashboard-farm/widget-abandoned-areas/widget-abandoned-areas.models';
import type { HeatmapOverTimeModel } from '../../../../pages/dashboard-farm/widget-heatmap-over-time/widget-heatmap-over-time.models';
import type { IndividualScouterModel } from '../../../../pages/dashboard-farm/widget-individual-scouter/widget-individual-scouter.models';
import type { MonitoringModel } from '../../../../pages/dashboard/widget-monitoring/widget-monitoring.models';
import { selectSelectedSeasons } from '../../../core.reducer';
import {
  LoadFailure,
  LoadSettingsFailure,
  LoadSettingsSuccess,
  LoadSuccess,
  PostSettingsFailure,
  PostSettingsSuccess,
  WidgetsActionsTypes,
  type LoadAllRequest,
  type LoadRequest,
  type LoadSettingsRequest,
  type PostSettingsRequest
} from './widgets.actions';
import { WidgetId, WidgetType, type WidgetsSettingsData } from './widgets.models';
import { loadSettingsMock, loadWidget, postSettingsMock } from './widgets.service';

type Keys = keyof Omit<WidgetsSettingsData, 'company_weather' | 'weather'>;

const mapper: Record<Keys, string> = {
  scouter_distance_time_avg: 'SCOUTER_STATS',
  tasks_list: 'PROPERTY_TASKS',
  abandoned_areas: 'PROPERTY_ABANDONED_AREAS',
  heatmap_over_time: 'DAYS_INDICATORS',
  scouter_performance_over_time: 'SCOUTER_DAILY_PERFORMANCE',
  individual_scouter: 'SCOUTER_PERFORMANCE_SUMMARY',
  tasks_property: 'PROPERTY_STATUS',
  most_common_issues_property: 'PROPERTY_MOST_COMMON_ISSUES',
  product_types: 'PROPERTY_SUMMARY'
};

const fullList: WidgetsSettingsData = {
  scouter_distance_time_avg: {
    index: 0,
    selected: true
  },
  tasks_list: {
    index: 1,
    selected: true
  },
  abandoned_areas: {
    index: 2,
    selected: true
  },
  heatmap_over_time: {
    index: 3,
    selected: true
  },
  scouter_performance_over_time: {
    index: 4,
    selected: true
  },
  individual_scouter: {
    index: 5,
    selected: true
  },
  tasks_property: {
    index: 6,
    selected: true
  },
  most_common_issues_property: {
    index: 7,
    selected: true
  },
  product_types: {
    index: 8,
    selected: true
  },
  company_weather: {
    index: 9,
    selected: true
  },
  weather: {
    index: 9,
    selected: true
  }
};

const CreateHandle =
  <T>(type: WidgetType, widgetId: WidgetId) =>
  (action$: ActionsObservable<ReturnType<typeof LoadAllRequest> | ReturnType<typeof LoadRequest>>, state$: StateObservable<AppState>) =>
    action$.pipe(
      filter(
        action =>
          // Action can be a request to load all widgets or a single widget
          (action.type === WidgetsActionsTypes.LOAD_ALL_REQUEST ||
            (action.type === WidgetsActionsTypes.LOAD_REQUEST &&
              // If we are loading a single widget the widget id must match
              action.payload.widgetId === widgetId)) &&
          // The type must also match so we don't load more widgets than we need to
          action.payload.widgetType === type
      ),
      map(action => action.payload.widgetRequest),
      withLatestFrom(
        state$.pipe(map(state => selectSelectedSeasons(state.uiState.global))),
        state$.pipe(map(state => state.uiState.global.systemFlags))
      ),
      switchMap(([widgetRequest, selectedSeasons, flags]) => {
        return loadWidget<T>(widgetId, widgetRequest, selectedSeasons, !!flags?.P40_25084_splitRequestDaysIndicators).pipe(
          map(response => response.data),
          map(data => {
            return LoadSuccess<T>(type, widgetId, data);
          }),
          catchError((error: string) => of(LoadFailure(type, widgetId, error)))
        );
      })
    );

const handleLoadWidgetsSettings = (action$: ActionsObservable<ReturnType<typeof LoadSettingsRequest>>) =>
  action$.pipe(
    ofType(WidgetsActionsTypes.LOAD_SETTINGS_REQUEST),
    exhaustMap(() =>
      loadSettingsMock().pipe(
        map(response => response.data),
        map(settings => {
          let currentDashboard = null;
          if (settings.dashboard_setups && settings.dashboard_setups.length > 0) {
            let count = Object.keys(fullList).length;
            (Object.keys(fullList) as Keys[]).forEach(key => {
              const widget = settings.dashboard_setups[0].widgets.find(w => w.name === mapper[key]);
              if (widget) {
                fullList[key].selected = true;
                fullList[key].index = widget.order;
              } else {
                fullList[key].selected = false;
                fullList[key].index = --count;
              }
            });
            currentDashboard = settings.dashboard_setups[0].id;
          }
          return LoadSettingsSuccess(fullList, currentDashboard);
        }),
        catchError((error: string) => {
          return of(LoadSettingsFailure(error));
        })
      )
    )
  );

export const handlePostWidgetsSettings = (
  action$: ActionsObservable<ReturnType<typeof PostSettingsRequest>>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(WidgetsActionsTypes.POST_SETTINGS_REQUEST),
    map(action => action.payload),
    withLatestFrom(
      state$.pipe(map(state => state.analytics.widgets.widgetsSettings?.currentDashboard)),
      state$.pipe(map(state => state.uiState.global.selectedCompany))
    ),
    concatMap(([data, currentDashboard, companyId]) => {
      if (!companyId) {
        return of(PostSettingsFailure('Failed to save widget settings: Company ID is missing.'));
      }

      if (!currentDashboard) {
        return of(PostSettingsFailure('Failed to save widget settings: current dashboard is missing.'));
      }

      const widgetsToSave: { name: string; order: number }[] = [];

      (Object.keys(data) as Keys[]).forEach(key => {
        if (data[key]?.selected === true) {
          widgetsToSave.push({
            name: mapper[key],
            order: data[key].index
          });
        }
      });

      const dashboardToSave = {
        name: 'Dashboard',
        company_id: companyId,
        widgets: widgetsToSave
      };

      const request = postSettingsMock({ id: currentDashboard, ...dashboardToSave });

      return request.pipe(
        map(() => {
          return PostSettingsSuccess();
        }),
        catchError((error: string) => of(PostSettingsFailure(error)))
      );
    })
  );

export default combineEpics<Epic>(
  // Widgets Settings
  handleLoadWidgetsSettings,
  handlePostWidgetsSettings,

  // Widget Monitoring
  CreateHandle<MonitoringModel[]>(WidgetType.COMPANY, WidgetId.MONITORING),

  // Widget Heatmap Over Time
  CreateHandle<HeatmapOverTimeModel[]>(WidgetType.PROPERTY, WidgetId.HEATMAP_OVER_TIME),

  // Widget Individual Scouter Performance
  CreateHandle<IndividualScouterModel[]>(WidgetType.PROPERTY, WidgetId.INDIVIDUAL_SCOUTER),

  // Widget Product Types
  CreateHandle<ProductTypesModel>(WidgetType.PROPERTY, WidgetId.PRODUCT_TYPES),

  // Widget Abandoned Areas
  CreateHandle<AbandonedAreasModel>(WidgetType.PROPERTY, WidgetId.ABANDONED_AREAS)
);
